/**
 * The filter slice as URL query params, so a view can be bookmarked and shared
 * (#1416).
 *
 * Readable named params rather than an encoded blob. The moment links exist
 * they are a compatibility surface — someone's bookmark has to keep working —
 * and named params can gain fields without breaking old links, can be read in a
 * log, and can be fixed by hand. A base64 blob fails all three: unreadable,
 * un-editable, and any shape change silently breaks every link already sent.
 *
 *   ?f.tier=enterprise&f.tier=strategic&criteria=Accuracy
 *   &score=0.2:0.8&groupBy=customer&window=7d&metric=p10
 *
 * Multi-valued filters repeat their param rather than joining with a delimiter,
 * so a metadata value containing a comma needs no escaping.
 *
 * The window is encoded as *intent* (`7d`, `1y`, `all`, or a frozen
 * `start:end`) — never as the dates it currently resolves to. A link that
 * carried resolved dates would freeze to the sender's day, so the recipient
 * would silently get a stale week (design doc 033).
 *
 * Decoding is deliberately total: unknown params are ignored and malformed
 * values fall back to the default rather than throwing. A truncated or
 * Slack-mangled link must degrade to a usable page, not a white screen.
 */
import type { PersistedFilterState } from "./filterPersistence";
import type { DateWindow } from "./dateWindow";
import { windowKey } from "./dateWindow";
import type { ScoreMetric } from "./chartData";
import type { TagFilter } from "../types";

/**
 * Metadata keys are customer-defined, so filters are namespaced. Without this a
 * tenant with a key called `window`, `groupBy` or `trace` would collide with a
 * reserved param and break the page — for that one customer only.
 */
const FILTER_PREFIX = "f.";
const CRITERIA = "criteria";
const SCORE = "score";
const GROUP_BY = "groupBy";
const WINDOW = "window";
const METRIC = "metric";

/** Params this codec owns. Anything else in the URL is left untouched. */
const OWNED = [CRITERIA, SCORE, GROUP_BY, WINDOW, METRIC];

const METRICS: ScoreMetric[] = ["avg", "p25", "p10"];

function encodeWindow(window: DateWindow): string | undefined {
  // An absolute window with neither end isn't a window; write no param at all.
  if (window.kind === "absolute" && !window.start && !window.end) return undefined;
  return windowKey(window) || undefined;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Shape *and* validity — the regex alone accepts `2026-13-99`, which would sail
 * through to the API as a date filter. Round-tripping through a Date rejects
 * impossible months and days (and 29 Feb in a non-leap year).
 */
function isRealDay(value: string): boolean {
  if (!ISO_DAY.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function decodeWindow(raw: string | null): DateWindow | undefined {
  if (!raw) return undefined;
  if (raw === "all") return { kind: "all" };

  const rolling = /^(\d+)([dy])$/.exec(raw);
  if (rolling) {
    const amount = Number(rolling[1]);
    if (amount > 0) {
      return {
        kind: "rolling",
        amount,
        unit: rolling[2] === "y" ? "year" : "day",
      };
    }
    return undefined;
  }

  if (raw.includes(":")) {
    const [start, end] = raw.split(":");
    const valid = (v: string) => v === "" || isRealDay(v);
    if (!valid(start) || !valid(end)) return undefined;
    if (!start && !end) return undefined;
    return { kind: "absolute", start: start || undefined, end: end || undefined };
  }
  return undefined;
}

function decodeScore(raw: string | null) {
  if (!raw || !raw.includes(":")) return undefined;
  const [min, max] = raw.split(":");
  const num = (v: string) => {
    if (v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : undefined;
  };
  const min_score = num(min);
  const max_score = num(max);
  if (min_score === undefined && max_score === undefined) return undefined;
  return { min_score, max_score };
}

/**
 * Write the slice into `params`, preserving any param this codec doesn't own
 * (`?trace=` on the Traces page, for instance).
 */
export function toSearchParams(
  state: PersistedFilterState,
  existing?: URLSearchParams,
): URLSearchParams {
  const params = new URLSearchParams(existing ?? undefined);

  for (const key of [...params.keys()]) {
    if (key.startsWith(FILTER_PREFIX) || OWNED.includes(key)) params.delete(key);
  }

  for (const filter of state.filters ?? []) {
    for (const value of filter.values) {
      params.append(`${FILTER_PREFIX}${filter.key}`, value);
    }
  }
  for (const value of state.criteriaFilter?.values ?? []) {
    params.append(CRITERIA, value);
  }
  if (state.scoreFilter) {
    const { min_score, max_score } = state.scoreFilter;
    if (min_score !== undefined || max_score !== undefined) {
      params.set(SCORE, `${min_score ?? ""}:${max_score ?? ""}`);
    }
  }
  if (state.groupByKey) params.set(GROUP_BY, state.groupByKey);
  if (state.dateWindow) {
    const encoded = encodeWindow(state.dateWindow);
    if (encoded) params.set(WINDOW, encoded);
  }
  if (state.scoreMetric) params.set(METRIC, state.scoreMetric);

  return params;
}

/**
 * A canonical, order-insensitive key for just the params this codec owns.
 *
 * Change detection has to ignore everything else in the query string, or
 * another feature's write looks like an external navigation: `useSelectedTrace`
 * setting `?trace=<id>` would otherwise be read as "the slice changed" and
 * re-apply the whole thing, handing every consumer fresh array identities for
 * an unchanged filter set.
 */
export function viewParamsKey(params: URLSearchParams): string {
  const owned: string[] = [];
  for (const [key, value] of params.entries()) {
    if (key.startsWith(FILTER_PREFIX) || OWNED.includes(key)) {
      owned.push(`${key}=${value}`);
    }
  }
  return owned.sort().join("&");
}

/** True when the URL carries a slice at all — i.e. it should win over storage. */
export function hasViewParams(params: URLSearchParams): boolean {
  for (const key of params.keys()) {
    if (key.startsWith(FILTER_PREFIX) || OWNED.includes(key)) return true;
  }
  return false;
}

/**
 * Read a slice out of `params`. Never throws: an unrecognised value is dropped
 * and the rest of the link still works.
 */
export function fromSearchParams(params: URLSearchParams): PersistedFilterState {
  const filters: TagFilter[] = [];
  for (const key of new Set(params.keys())) {
    if (!key.startsWith(FILTER_PREFIX)) continue;
    const name = key.slice(FILTER_PREFIX.length);
    const values = params.getAll(key).filter(Boolean);
    if (name && values.length) filters.push({ key: name, values });
  }

  const criteria = params.getAll(CRITERIA).filter(Boolean);
  const metric = params.get(METRIC) as ScoreMetric | null;

  return {
    filters,
    criteriaFilter: criteria.length ? { values: criteria } : undefined,
    scoreFilter: decodeScore(params.get(SCORE)),
    groupByKey: params.get(GROUP_BY) || undefined,
    dateWindow: decodeWindow(params.get(WINDOW)),
    scoreMetric: metric && METRICS.includes(metric) ? metric : undefined,
  };
}
